// Carer home — the list of visits assigned to the signed-in carer.
// Same query the React Native app uses (status=new,assigned,scheduled,in_progress).

import { type JSX } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  carerApi, clearCarerToken,
  SERVICE_LABELS, STATUS_CONFIG,
  type Visit,
} from "../../lib/carerApi.js";
import { CarerNotificationBell } from "./CarerNotificationBell.js";

interface VisitListResponse { data: Visit[]; total: number; page: number; limit: number }

const STATUS_FALLBACK = { label: "New", color: "text-amber-700", bg: "bg-amber-50 border-amber-200" };

function formatDate(dt: string): string {
  return new Date(dt).toLocaleDateString("en-IN",
    { weekday: "short", day: "numeric", month: "short" });
}

function formatTime(dt: string): string {
  return new Date(dt).toLocaleTimeString("en-IN",
    { hour: "2-digit", minute: "2-digit", hour12: true });
}

function VisitCard({ visit, onSelect }: { visit: Visit; onSelect: () => void }): JSX.Element {
  const status = STATUS_CONFIG[visit.status] ?? STATUS_CONFIG["new"] ?? STATUS_FALLBACK;
  const patientLabel = visit.patient_name && visit.patient_name.trim().length > 0
    ? visit.patient_name
    : `Patient …${visit.patient_id.slice(-6)}`;
  return (
    <button
      onClick={onSelect}
      className="w-full bg-white rounded-2xl shadow-sm border border-slate-200
                 p-4 text-left hover:shadow-md active:scale-[0.99] transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 truncate">{patientLabel}</p>
          <p className="text-navy-700 text-sm font-medium mt-0.5">
            {SERVICE_LABELS[visit.service_type] ?? visit.service_type}
          </p>
          {visit.address && (
            <p className="text-slate-500 text-xs mt-1 truncate">📍 {visit.address}</p>
          )}
        </div>
        <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border ${status.bg} ${status.color}`}>
          {status.label}
        </span>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4">
        <span className="text-slate-600 text-sm">📅 {formatDate(visit.scheduled_datetime)}</span>
        <span className="text-slate-600 text-sm">🕐 {formatTime(visit.scheduled_datetime)}</span>
      </div>
    </button>
  );
}

export function CarerHomePage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch, isFetching } = useQuery<VisitListResponse>({
    queryKey: ["carer-visits"],
    queryFn:  () => carerApi.getMyVisits(),
  });

  const visits = data?.data ?? [];

  function handleSignOut(): void {
    clearCarerToken();
    navigate("/carer/login", { replace: true });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy-800 text-white sticky top-0 z-10 shadow-md"
              style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">My Visits</h1>
            <p className="text-navy-200 text-xs">
              {visits.length} active {visits.length === 1 ? "visit" : "visits"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CarerNotificationBell />
            <button
              onClick={() => { void refetch(); }}
              disabled={isFetching}
              className="text-navy-200 hover:text-white transition-colors text-sm disabled:opacity-60"
              aria-label="Refresh"
            >
              {isFetching ? "…" : "↻ Refresh"}
            </button>
            <button onClick={handleSignOut}
              className="text-navy-200 hover:text-white transition-colors text-sm">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-4 space-y-3">
        {isLoading && (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-navy-700 border-t-transparent rounded-full animate-spin"
                 aria-label="Loading" />
            <p className="text-slate-500 text-sm mt-3">Loading visits…</p>
          </div>
        )}

        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
            <p className="text-red-700 text-sm">Failed to load visits.</p>
            <button onClick={() => { void refetch(); }}
              className="text-navy-700 text-sm mt-2 hover:underline">
              Try again
            </button>
          </div>
        )}

        {!isLoading && !isError && visits.length === 0 && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4" role="img" aria-label="Empty">📋</div>
            <p className="text-slate-600 font-medium">No visits assigned</p>
            <p className="text-slate-400 text-sm mt-1">Check back later or contact your coordinator</p>
          </div>
        )}

        {visits.map((visit) => (
          <VisitCard
            key={visit._id}
            visit={visit}
            onSelect={() => navigate(`/carer/visit/${visit._id}`)}
          />
        ))}
      </main>
    </div>
  );
}
